import React, { useEffect, useState, useRef } from "react";
import { withRouter } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import _ from "lodash";
import moment from "moment-timezone";
import isMatch from "date-fns/isMatch";
import * as Sentry from "@sentry/browser";

import api from "../../libs/api";
import * as billing from "../../libs/billing";
import { setClient } from "../../redux/client";
import { ReactComponent as AlertIcon } from "../../assets/images/alert-info.svg";

import { Preloader, Oval, ThreeDots } from "react-preloader-icon";
import { Alert, TextField } from "@magic-engineering/components";

import ClientCard from "../Card/ClientCard";

import Field from "../Form/Field";
import PairedFields from "../Form/PairedFields";

import DescriptionField from "../Form/DescriptionField";
import ImageField from "../Form/ImageField";
import SelectField from "../Form/SelectField";
import SelectQueryField from "../Form/SelectQueryField";

import CheckboxField from "../Form/CheckboxField";
import RadioField from "../Form/RadioField";
import CustomSelect from "../Form/CustomSelect";

import AnimationWrapper from "../Form/AnimationWrapper";
import FieldWrapper from "../Form/FieldWrapper";

import AboutBuilder from "./AboutBuilder";

import PaymentDetails from "./PaymentDetails";

import fields, { checkboxOptions } from "./fields";
import styles from "./EditProfile.module.scss";

function EditProfile({ client, history }) {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    shouldFocusError: false,
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [regions, setRegions] = useState([]);

  const [countryCode, setCountryCode] = useState(null);
  const [regionCode, setRegionCode] = useState(null);
  const [cityCode, setCityCode] = useState(null);

  // checks if the location fields were already loaded to avoid hiding and displaying them
  const [alreadyLoaded, setAlreadyLoaded] = useState({});

  // checks if the location fields are currently loading
  const [isLoading, setIsLoading] = useState({});

  // checks if region has cities
  const [regionHasCities, setRegionHasCities] = useState(true);

  const [profile, setProfile] = useState(null);

  // Stores communication app states
  const [commAppVisibility, setCommAppVisibility] = useState([]);
  const [commAppHandles, setCommAppHandles] = useState([]);

  const [location, setLocation] = useState({});

  const [showAlert, setShowAlert] = useState(true);

  const data = watch();

  const [needsPaymentDetails, setNeedsPaymentDetails] = useState(true);
  const paymentDetailsRef = useRef(null);

  let visibilities = {
    name: true,
    nickname: true,
    country_id: true,
  };

  const message_me =
    data["message_me"] instanceof Array
      ? data["message_me"]
      : JSON.parse(data["message_me"] || "[]");
  const tools = data["tools"] instanceof Array ? data["tools"] : JSON.parse(data["tools"] || "[]");

  const hasName = !!data["name"];
  const hasRegions = alreadyLoaded["regions"] || regions.length > 0;
  const hasCities = alreadyLoaded["cities"] || (data["region_id"] && cities.length > 0);
  const hasLocation =
    !!data["country_id"] && !!data["region_id"] && (!!data["city_id"] || !regionHasCities);
  const hasTimezone = !!data["timezone"] && (profile || hasLocation);
  const hasCompany = !!data["company"] && hasTimezone;
  const hasCompanySize = !!data["company_size"] && hasCompany;
  const hasSexAsOther = data["sex"] === "sex_others";
  const hasRoleAsOther = data["role"] === "role_others";
  const hasNeedAsOther = data["need"] === "need_others";
  const hasGoalAsOther = data["goal"] === "goal_others";
  const hasOtherTools = tools.includes("Other(s)");
  const hasOtherCommTimes = message_me.includes("Other(s)");

  visibilities = {
    ...visibilities,
    photo_url: hasName,
    region_id: hasRegions,
    cities: hasCities,
    timezone: hasLocation,
    birthdate: hasTimezone,
    company_size: hasCompany,
    others: hasCompanySize,
    sex_others: hasSexAsOther,
    role_others: hasRoleAsOther,
    need_others: hasNeedAsOther,
    goal_others: hasGoalAsOther,
    tools_others: hasOtherTools,
    message_others: hasOtherCommTimes,
    payment_details: needsPaymentDetails && hasCompanySize,
  };

  const isVisible = (key, isOtherField) => {
    if (isOtherField) return visibilities[key];
    if (profile) return !!profile[key] || visibilities[key];
    return visibilities[key];
  };

  useEffect(() => {
    const checkCards = async () => {
      try {
        const { data } = await api.get("/clients/cards");
        setNeedsPaymentDetails(data?.length === 0);
      } catch (e) {
        Sentry.withScope((scope) => {
          scope.setLevel("warning");
          Sentry.captureMessage(e);
        });
      }
    };

    checkCards();
  }, []);

  // initial load
  useEffect(() => {
    async function fetchCountries() {
      const { data } = await api.get("/clients/profile/countries");
      setCountries(data);
    }

    fetchCountries();
  }, []);

  // once countries are loaded
  useEffect(() => {
    if (profile && profile.country_name && countries.length > 0) {
      const match = countries.find((c) => c.name === profile.country_name) || {};

      setCountryCode(match.country_id || countries[0].country_id);
      setLocation((location) => ({
        ...location,
        country: match.name || countries[0].name,
      }));
    }

    if (profile) {
      setAlreadyLoaded({
        regions: !!profile.region_name,
        cities: !!profile.city_name,
      });
    }
  }, [profile, countries]);

  // when a country is selected
  useEffect(() => {
    async function fetchCountry(countryCode) {
      const { data } = await api.get(`/clients/profile/countries/${countryCode}`);
      const { regions } = data;

      setRegions(regions);
      setAlreadyLoaded((a) => ({ ...a, regions: true }));
      setIsLoading((l) => ({ ...l, regions: false }));

      setLocation((location) => ({
        ...location,
        country: (countries.find((c) => c.country_id === parseInt(countryCode)) || {}).name,
      }));
    }

    if (countryCode) {
      setCities([]);
      setRegions([]);
      setRegionHasCities(true);

      setCityCode(null);
      setRegionCode(null);

      setIsLoading((l) => ({ ...l, regions: true }));

      setLocation((location) => ({
        ...location,
        region: "",
        city: "",
      }));

      fetchCountry(countryCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  // once regions are loaded
  useEffect(() => {
    if (profile && profile.region_name && regions.length > 0) {
      const match = regions.find((region) => region.name === profile.region_name) || {};
      const region = match.region_name || regions[0].name;
      const regionCode = match.region_id || regions[0].region_id;

      setRegionCode(regionCode);
      setLocation((location) => ({
        ...location,
        region: region,
      }));
    } else {
      setRegionCode(null);
    }
  }, [profile, regions]);

  // when a region is selected
  useEffect(() => {
    async function fetchRegion(regionCode) {
      const { data } = await api.get(`/clients/profile/regions/${regionCode}`);
      const { cities } = data;

      setCities(cities);
      setRegionHasCities(cities.length);

      setAlreadyLoaded((a) => ({ ...a, cities: true }));
      setIsLoading((l) => ({ ...l, cities: false }));

      setLocation((location) => ({
        ...location,
        region: (regions.find((r) => r.region_id === regionCode) || {}).name,
      }));
    }

    if (regionCode) {
      setCities([]);
      setCityCode(null);

      setIsLoading((l) => ({ ...l, cities: true }));

      setLocation((location) => ({
        ...location,
        city: "",
      }));

      fetchRegion(regionCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionCode]);

  // once cities are loaded
  useEffect(() => {
    if (profile && profile.city_name && cities.length > 0) {
      const match = cities.find((city) => city.name === profile.city_name) || {};
      const city = match.city_name || cities[0].name;
      const cityCode = match.city_id || cities[0].city_id;

      setCityCode(cityCode);
      setLocation((location) => ({
        ...location,
        city: city,
      }));
    } else {
      setCityCode(null);
    }
  }, [profile, cities]);

  // when a city is selected
  useEffect(() => {
    if (cityCode) {
      setLocation((location) => ({
        ...location,
        city: (cities.find((c) => c.city_id === cityCode) || {}).name,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityCode]);

  useEffect(() => {
    const setInitialValues = (initialData) => {
      if (initialData) {
        // Set initial values for `other` fields
        ["sex", "role", "need", "goal", "message_me", "tools"].forEach((f) => {
          if (!initialData[f]) return;

          const defaultCheckboxOption = checkboxOptions[f].default;
          if (f === "message_me" || f === "tools") {
            const selectedDefaultValues = (initialData[f] || []).filter((val) =>
              defaultCheckboxOption.find((opt) => opt.name === val)
            );
            const otherValues = (initialData[f] || []).filter(
              (val) => !defaultCheckboxOption.find((opt) => opt.name === val)
            );
            const othersKey = f === "message_me" ? "message" : f;

            initialData[`${othersKey}_others`] = otherValues;
            initialData[f] = selectedDefaultValues.concat(otherValues.length ? "Other(s)" : []);
          } else if (!defaultCheckboxOption.find((opt) => opt.name === initialData[f])) {
            initialData[`${f}_others`] = initialData[f];
            initialData[f] = `${f}_others`;
          }
        });
      }

      // For setting initial communication apps state
      const { communication = [] } = initialData || {};
      const { default: commDefaultOptions, other: otherOption } =
        checkboxOptions.communication_visibility;
      const otherCommApps = (communication || []).filter(
        (c) => !commDefaultOptions.find((opt) => opt.name === c.app)
      );
      const toggledCommOptions = (communication || [])
        .filter((c) => commDefaultOptions.find((opt) => opt.name === c.app))
        .map((c) => c.app)
        .concat(otherCommApps.length ? "Other(s)" : []);

      const initialCommAppHandles = commDefaultOptions.map((c) => {
        return {
          app: c.name,
          handle: ((communication || []).find((com) => com.app === c.name) || {}).handle || "",
          label: (c.conditional || {}).label || "",
          placeholder: (c.conditional || {}).placeholder || "",
        };
      });

      initialCommAppHandles.push({
        app: otherCommApps.length ? otherCommApps[0].app : "",
        handle: otherCommApps.length ? otherCommApps[0].handle : "",
        label: (otherOption[0].conditional || {}).label || "",
        placeholder: (otherOption[0].conditional || {}).placeholder || "",
        isOtherOption: true,
      });

      setCommAppVisibility(toggledCommOptions);
      setCommAppHandles(initialCommAppHandles);

      return initialData;
    };

    async function fetchProfile() {
      try {
        const { data = {} } = await api.get("/clients/profile");
        const initialData = setInitialValues(data.profile);

        setProfile(initialData || null);
        setClient(data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    if (!profile) {
      fetchProfile();
    }
  }, [profile]);

  const onCountryChange = (e) => {
    setCountryCode(parseInt(e.target.value));
  };

  const onRegionChange = (e) => {
    setRegionCode(parseInt(e.target.value));
  };

  const onCityChange = (e) => {
    setCityCode(parseInt(e.target.value));
  };

  const onSubmit = async (form) => {
    setSubmitting(true);

    if (needsPaymentDetails) {
      try {
        await billing.addCardToStripe(paymentDetailsRef);
      } catch (error) {
        toast.error(
          _.get(error, "response.data.message") || error.message || "Failed to save card."
        );

        Sentry.withScope((scope) => {
          scope.setLevel("warning");
          Sentry.captureMessage(error);
        });

        setSubmitting(false);
        return;
      }
    }

    try {
      // Only save data that is visible in the form
      form.communication = (form.communication || []).filter(
        (c) =>
          c.app &&
          c.handle &&
          // Check if it was toggled, or if it is an Other option
          (commAppVisibility.includes(c.app) ||
            (commAppVisibility.includes("Other(s)") &&
              !checkboxOptions.communication_visibility.default.find((opt) => opt.name === c.app)))
      );

      // Sequence is unpredictable, dont remove ommitted fields in this loop
      for (const name in fields) {
        const field = fields[name];

        // for conditionally replacing data with `other` values
        if (!!form[name] && typeof field.replaceField === "function" && field.replaceField(form)) {
          form[field.replaceField(form)] = form[name];
        }

        if (!!form[name] && typeof field.appendField === "function" && field.appendField(form)) {
          form[field.appendField(form)] = [...form[field.appendField(form)], ...form[name]];
        }
      }

      for (const name in fields) {
        const field = fields[name];

        if (field.type === "checkbox" && field.isRequired) {
          const omittedChoices = field.choices
            .flat()
            .filter(({ isOmitted }) => isOmitted)
            .map(({ name }) => name);
          form[name] = form[name].filter((chosen) => !omittedChoices.includes(chosen));
        }

        if (field.isOmitted) {
          delete form[name];
        }
      }

      delete form.communication_visibility;
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setLevel("warning");
        Sentry.captureMessage(error);
      });
    }

    // for replacing location id's with their names
    delete form["country_id"];
    delete form["region_id"];
    delete form["city_id"];

    form["country_name"] = location.country;
    form["region_name"] = location.region;
    form["city_name"] = location.city;

    // do not submit blank birthdate
    if (!form["birthdate"]) {
      delete form["birthdate"];
    }

    api
      .post("/clients/update", form)
      .then(function (response) {
        toast.success("Profile successfully saved!");
        setSubmitting(false);

        if (!profile || Object.keys(profile).length === 0) {
          history.push("/");
        } else {
          const { data } = response;
          setClient({ ...client, profile: data });
        }
      })
      .catch(function (error) {
        toast.error("Error saving profile! Please check your fields and try again.");
        setSubmitting(false);
      });
  };

  const onError = (error) => {
    window.scrollTo(0, 0);
  };

  const getSystemTimezoneOffset = () => {
    const userTz = moment.tz.guess(true);
    const zone = moment.tz.zone(userTz);
    const offset = zone.offsets.slice(-1)[0];
    return (offset / 60) * -1;
  };

  const getDefaultValue = (field) => {
    return !profile || Object.keys(profile).length === 0 ? "" : profile[field] || "";
  };

  if (loading) {
    return (
      <div
        id="editProfile"
        className={`container my-0 mx-auto ${styles.container}`}
        data-testid="profile-preloader"
      >
        <div className={styles.header}>
          <h2 className="mg-b-l">My profile</h2>
          <Preloader use={Oval} size={60} strokeWidth={6} strokeColor="#262626" duration={2000} />
        </div>
      </div>
    );
  }

  return (
    <div
      id="editProfile"
      className={`container-fluid container-xl container-lg my-0 mx-auto px-0 ${styles.container}`}
      data-testid="profile-loaded"
    >
      <div className={styles.header}>
        <h2 className="mg-b-l">My profile</h2>
        {(!profile || Object.keys(profile).length === 0) && showAlert ? (
          <div className="d-flex align-items-start">
            <Alert
              className={`mb-5 ${styles.alert}`}
              title={
                <span className="d-flex align-items-center mx-2">
                  <AlertIcon className="mb-1 mr-2" />
                  Congratulations on your new Magic service
                </span>
              }
              text={
                <ul className="mx-2 my-2">
                  <li>Please complete your profile to access your Client Portal Services.</li>
                  <li>
                    Your assistant will review your profile to personalize their work for you.
                  </li>
                </ul>
              }
              buttonLabel="Close"
              onClick={() => setShowAlert(false)}
              onDismiss={() => setShowAlert(false)}
            />
          </div>
        ) : null}
      </div>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div id="cards" className={`mb-4 ${styles.cards}`}>
          {Object.keys(errors).length ? (
            <div className="alert alert-danger" role="alert">
              There was a problem with your submission. Errors have been highlighted below.
            </div>
          ) : null}
          <div className="card">
            <div className="row no-gutters">
              <div className="col-12">
                <span className="header d-block">Basic Information</span>
              </div>
            </div>
            <div className="form-row no-gutters">
              <AnimationWrapper
                className="col-12 col-xl-6 col-lg-6 col-md-6"
                isVisible={isVisible("nickname")}
              >
                <Field
                  {...fields.name}
                  ref={register({ required: true })}
                  errors={errors}
                  defaultValue={client.name || getDefaultValue("name")}
                  placeholder={client.name}
                  isVisible={isVisible("name")}
                />
              </AnimationWrapper>
              <AnimationWrapper
                className="col-12 col-xl-6 col-lg-6 col-md-6"
                isVisible={isVisible("nickname")}
              >
                <Field
                  {...fields.nickname}
                  ref={register({ required: false })}
                  errors={errors}
                  defaultValue={getDefaultValue("nickname")}
                  placeholder={(client.name || "").split(" ")[0]}
                />
              </AnimationWrapper>
            </div>
            {
              <div className="form-row no-gutters">
                <AnimationWrapper
                  className="col-12 col-xl-6 col-lg-6 col-md-6"
                  isVisible={isVisible("photo_url")}
                >
                  <Controller
                    name={"photo_url"}
                    control={control}
                    defaultValue={getDefaultValue("photo_url")}
                    render={(props) => (
                      <ImageField
                        {...fields.photo_url}
                        errors={errors}
                        src={(profile || {}).photo_url}
                        onChange={(photo_url) => {
                          props.onChange(photo_url);
                        }}
                      />
                    )}
                  />
                </AnimationWrapper>
              </div>
            }
            <div className="form-row no-gutters">
              <AnimationWrapper
                className="col-12 col-xl-6 col-lg-6 col-md-6"
                isVisible={isVisible("country_id")}
              >
                <SelectQueryField
                  {...fields.country}
                  ref={register({ required: true })}
                  onChange={onCountryChange}
                  choices={countries}
                  choiceKey="country_id"
                  choiceValue="name"
                  errors={errors}
                  defaultValue={countryCode}
                />
              </AnimationWrapper>
              {isLoading["regions"] && !alreadyLoaded["regions"] ? (
                <FieldWrapper className="col-12 col-xl-6 col-lg-6 col-md-6 mt-5 d-flex justify-content-center">
                  <Preloader
                    use={ThreeDots}
                    size={30}
                    strokeWidth={6}
                    strokeColor="#262626"
                    duration={2000}
                  />
                </FieldWrapper>
              ) : null}
              <AnimationWrapper
                className="col-12 col-xl-6 col-lg-6 col-md-6"
                isVisible={isVisible("region_id")}
              >
                <SelectQueryField
                  {...fields.region}
                  ref={register({ required: true })}
                  onChange={onRegionChange}
                  choices={regions}
                  choiceKey="region_id"
                  choiceValue="name"
                  errors={errors}
                  defaultValue={regionCode}
                />
              </AnimationWrapper>
            </div>
            <div className="form-row no-gutters">
              {isLoading["cities"] && !alreadyLoaded["cities"] ? (
                <FieldWrapper className="col-12 col-xl-6 col-lg-6 col-md-6 mt-5 d-flex justify-content-center">
                  <Preloader
                    use={ThreeDots}
                    size={30}
                    strokeWidth={6}
                    strokeColor="#262626"
                    duration={2000}
                  />
                </FieldWrapper>
              ) : null}
              <AnimationWrapper
                className="col-12 col-xl-6 col-lg-6 col-md-6"
                isVisible={isVisible("cities")}
              >
                {regionHasCities ? (
                  <SelectQueryField
                    {...fields.city}
                    ref={register({ required: true })}
                    onChange={onCityChange}
                    choices={cities}
                    choiceKey="city_id"
                    choiceValue="name"
                    errors={errors}
                    defaultValue={cityCode}
                  />
                ) : null}
              </AnimationWrapper>
              <AnimationWrapper
                className="col-12 col-xl-6 col-lg-6 col-md-6"
                isVisible={isVisible("timezone")}
              >
                <SelectQueryField
                  className="col-12 col-xl-6 col-lg-6 col-md-6"
                  {...fields.timezone}
                  ref={register({ required: true })}
                  errors={errors}
                  defaultValue={getDefaultValue("timezone")}
                  hint={getSystemTimezoneOffset()}
                />
              </AnimationWrapper>
            </div>
            <AnimationWrapper isVisible={isVisible("birthdate")}>
              <div className="form-row no-gutters">
                <Controller
                  name={"birthdate"}
                  control={control}
                  defaultValue={getDefaultValue("birthdate")}
                  rules={{
                    validate: (value) => {
                      if (!value) return true;
                      return isMatch(value, "MM/dd/yyyy");
                    },
                  }}
                  render={(props) => (
                    <Field
                      className="col-12 col-xl-3 col-lg-3 col-md-3"
                      {...fields.birthdate}
                      ref={register({ required: false })}
                      errors={errors}
                      defaultValue={
                        getDefaultValue("birthdate")
                          ? new Date(getDefaultValue("birthdate")).toLocaleDateString("en-US", {
                              month: "2-digit",
                              day: "2-digit",
                              year: "numeric",
                            })
                          : ""
                      }
                      onChange={(value) => props.onChange(value)}
                    />
                  )}
                />
                <div className="col-3" />
                <Controller
                  name={"sex"}
                  control={control}
                  defaultValue={getDefaultValue("sex")}
                  rules={{ required: false }}
                  render={(props) => (
                    <RadioField
                      choiceClassName="col-12 col-xl col-lg col-md"
                      className="col-12 col-xl-6 col-lg-6 col-md-6"
                      {...fields.sex}
                      errors={errors}
                      defaultValue={getDefaultValue("sex")}
                      onChange={(value) => props.onChange(value)}
                    />
                  )}
                />
              </div>
              <div className="form-row no-gutters">
                <div className="col-12 col-xl-6 col-lg-6 col-md-6" />
                <AnimationWrapper isVisible={isVisible("sex_others", true)}>
                  <Controller
                    name={"sex_others"}
                    control={control}
                    defaultValue={getDefaultValue("sex_others")}
                    rules={{
                      validate: (value, other) => (isVisible("sex_others", true) ? !!value : true),
                    }}
                    render={(props) => (
                      <Field
                        className="col-12 col-xl-6 col-lg-6 col-md-6"
                        {...fields.sex_others}
                        errors={errors}
                        defaultValue={getDefaultValue("sex_others")}
                        onChange={(value) => props.onChange(value)}
                      />
                    )}
                  />
                </AnimationWrapper>
              </div>
            </AnimationWrapper>
          </div>
          <AnimationWrapper isVisible={isVisible("birthdate")}>
            <div className="card">
              <div className="row no-gutters">
                <div className="col-12">
                  <span className="header d-block">About</span>
                </div>
              </div>
              <div className="form-row no-gutters">
                <FieldWrapper className="col-12">
                  <DescriptionField
                    {...fields.about_me}
                    description={<AboutBuilder data={data || profile} />}
                  />
                </FieldWrapper>
              </div>
              <div className="form-row no-gutters">
                <Controller
                  name={"role"}
                  control={control}
                  defaultValue={getDefaultValue("role")}
                  rules={{ required: true }}
                  render={(props) => (
                    <RadioField
                      choiceClassName="col-12 col-xl col-lg col-md capitalize"
                      className="form-group col-12"
                      {...fields.role}
                      errors={errors}
                      defaultValue={getDefaultValue("role")}
                      onChange={(value) => props.onChange(value)}
                    />
                  )}
                />
              </div>
              <div className="form-row no-gutters">
                <AnimationWrapper isVisible={isVisible("role_others", true)}>
                  <Controller
                    name={"role_others"}
                    control={control}
                    defaultValue={getDefaultValue("role_others")}
                    rules={{
                      validate: (value) => (isVisible("role_others", true) ? !!value : true),
                    }}
                    render={(props) => (
                      <TextField
                        {...fields.role_others}
                        maxLength={200}
                        onChange={(value) => props.onChange(value)}
                        errors={errors}
                        defaultValue={getDefaultValue("role_others")}
                      />
                    )}
                  />
                </AnimationWrapper>
              </div>
              <div className="form-row no-gutters">
                <FieldWrapper className="col-12 col-xl-6 col-lg-6 col-md-6">
                  <Field
                    {...fields.company}
                    ref={register({ required: true })}
                    errors={errors}
                    defaultValue={getDefaultValue("company")}
                  />
                </FieldWrapper>
                <AnimationWrapper
                  className="col-12 col-xl-6 col-lg-6 col-md-6"
                  isVisible={isVisible("company_size")}
                >
                  <SelectField
                    {...fields.company_size}
                    ref={register({ required: true })}
                    errors={errors}
                    defaultValue={getDefaultValue("company_size")}
                    isVisible={isVisible("company_size")}
                  />
                </AnimationWrapper>
              </div>
              <AnimationWrapper isVisible={isVisible("others", true)}>
                <div className="form-row no-gutters">
                  <Controller
                    name={"need"}
                    control={control}
                    defaultValue={getDefaultValue("need")}
                    rules={{ required: false }}
                    render={(props) => (
                      <RadioField
                        choiceClassName="col-12 col-xl col-lg col-md"
                        className="col-12 col-xl-6 col-lg-6 col-md-6"
                        {...fields.need}
                        errors={errors}
                        defaultValue={getDefaultValue("need")}
                        onChange={(value) => props.onChange(value)}
                      />
                    )}
                  />
                  <Controller
                    name={"goal"}
                    control={control}
                    defaultValue={getDefaultValue("goal")}
                    rules={{ required: false }}
                    render={(props) => (
                      <RadioField
                        choiceClassName="col-12 col-xl col-lg col-md"
                        className="col-12 col-xl-6 col-lg-6 col-md-6"
                        {...fields.goal}
                        errors={errors}
                        defaultValue={getDefaultValue("goal")}
                        onChange={(value) => props.onChange(value)}
                      />
                    )}
                  />
                </div>
                <div className="form-row no-gutters">
                  <AnimationWrapper
                    className="col-12 col-xl-6 col-lg-6 col-md-6"
                    isVisible={isVisible("need_others", true)}
                  >
                    <Controller
                      name={"need_others"}
                      control={control}
                      defaultValue={getDefaultValue("need_others")}
                      rules={{
                        validate: (value) => (isVisible("need_others", true) ? !!value : true),
                      }}
                      render={(props) => (
                        <TextField
                          {...fields.need_others}
                          errors={errors}
                          maxLength={200}
                          onChange={(value) => props.onChange(value)}
                          defaultValue={getDefaultValue("need_others")}
                        />
                      )}
                    />
                  </AnimationWrapper>
                  <AnimationWrapper
                    className="col-12 col-xl-6 col-lg-6 col-md-6"
                    isVisible={isVisible("goal_others", true)}
                  >
                    <Controller
                      name={"goal_others"}
                      control={control}
                      defaultValue={getDefaultValue("goal_others")}
                      rules={{
                        validate: (value) => (isVisible("goal_others", true) ? !!value : true),
                      }}
                      render={(props) => (
                        <TextField
                          {...fields.goal_others}
                          maxLength={200}
                          onChange={(value) => props.onChange(value)}
                          errors={errors}
                          defaultValue={getDefaultValue("goal_others")}
                        />
                      )}
                    />
                  </AnimationWrapper>
                </div>
              </AnimationWrapper>
            </div>
            <AnimationWrapper className="card" isVisible={isVisible("others", true)}>
              <div className="row no-gutters">
                <div className="col-12">
                  <span className="header d-block">Preferences</span>
                </div>
              </div>
              <div className="form-row no-gutters">
                <Controller
                  name={"message_me"}
                  control={control}
                  defaultValue={getDefaultValue("message_me")}
                  rules={{
                    required: false,
                    validate: (value) => {
                      if (!value) return true;
                      const obj = value instanceof Object ? value : JSON.parse(value);
                      return Object.keys(obj).filter((v) => obj[v]).length > 0;
                    },
                  }}
                  render={(props) => (
                    <CheckboxField
                      choiceClassName="col-12 col-xl col-lg col-md"
                      className="form-group col-12 col-xl-6 col-lg-6 col-md-6"
                      {...fields.message_me}
                      onChange={(values) => {
                        props.onChange(values);
                      }}
                      errors={errors}
                      values={getDefaultValue("message_me")}
                    />
                  )}
                />
                <Controller
                  name="communication_visibility"
                  control={control}
                  defaultValue={commAppVisibility}
                  rules={{
                    required: false,
                    validate: (value) => {
                      if (!value) return true;
                      if (Array.isArray(value) && value.length === 0) return true;
                      const obj = value instanceof Object ? value : JSON.parse(value);
                      return Object.keys(obj).filter((v) => obj[v]).length > 0;
                    },
                  }}
                  render={(props) => (
                    <CheckboxField
                      choiceClassName="col-12 col-xl col-lg col-md"
                      className="form-group col-12 col-xl-6 col-lg-6 col-md-6"
                      {...fields.communication_visibility}
                      onChange={(values) => {
                        props.onChange(values);
                        setCommAppVisibility(values);
                      }}
                      errors={errors}
                      values={commAppVisibility}
                    />
                  )}
                />
              </div>
              <div className="form-row no-gutters">
                <AnimationWrapper
                  className="col-12 col-xl-6 col-lg-6 col-md-6"
                  isVisible={isVisible("message_others", true)}
                >
                  <Controller
                    name="message_others"
                    control={control}
                    defaultValue={getDefaultValue("message_others")}
                    render={(props) => (
                      <CustomSelect
                        {...fields.message_others}
                        defaultValue={getDefaultValue("message_others")}
                        errors={errors}
                        onChange={(values) => {
                          props.onChange(values);
                        }}
                      />
                    )}
                  />
                </AnimationWrapper>
              </div>
              <div className="form-row no-gutters" data-testid="messageMe">
                {commAppHandles.map((commAppHandle, idx) => (
                  <AnimationWrapper
                    key={`communicationApp-${idx}`}
                    className="col-12"
                    isVisible={commAppVisibility.includes(
                      commAppHandle.isOtherOption ? "Other(s)" : commAppHandle.app
                    )}
                  >
                    <PairedFields
                      control={control}
                      keyFieldParams={{
                        id: `communication[${idx}].app`,
                        name: `communication[${idx}].app`,
                        isRequired: commAppVisibility.includes(
                          commAppHandle.isOtherOption ? "Other(s)" : commAppHandle.app
                        ),
                        defaultValue: commAppHandle.app || "",
                        disabled: !commAppHandle.isOtherOption,
                        placeholder: commAppHandle.placeholder,
                        type: "text",
                        isInvalid:
                          errors.communication &&
                          errors.communication[idx] &&
                          errors.communication[idx].app
                            ? "is-invalid"
                            : "",
                        className: "col-12 col-xl-4 col-lg-6 col-md-6",
                      }}
                      valueFieldParams={{
                        id: `communication[${idx}].handle`,
                        name: `communication[${idx}].handle`,
                        defaultValue: commAppHandle.handle,
                        isRequired: commAppVisibility.includes(
                          commAppHandle.isOtherOption ? "Other(s)" : commAppHandle.app
                        ),
                        placeholder: commAppHandle.placeholder,
                        type: "text",
                        isInvalid:
                          errors.communication &&
                          errors.communication[idx] &&
                          errors.communication[idx].handle
                            ? "is-invalid"
                            : "",
                        className: "col-12 col-xl-8 col-lg-6 col-md-6",
                      }}
                      label={commAppHandle.label}
                    />
                  </AnimationWrapper>
                ))}
              </div>
              <div className="form-row no-gutters">
                <Controller
                  name="tools"
                  control={control}
                  defaultValue={getDefaultValue("tools")}
                  render={(props) => (
                    <CheckboxField
                      choiceClassName="col-12 col-xl col-lg col-md"
                      className="form-group col-12"
                      dataTestId="tools"
                      {...fields.tools}
                      onChange={(values) => {
                        props.onChange(values);
                      }}
                      errors={errors}
                      values={getDefaultValue("tools")}
                    />
                  )}
                />
              </div>
              <div className="form-row no-gutters">
                <AnimationWrapper
                  className="col-12 col-xl-6 col-lg-6 col-md-6"
                  isVisible={isVisible("tools_others", true)}
                >
                  <Controller
                    name="tools_others"
                    control={control}
                    defaultValue={getDefaultValue("tools_others")}
                    render={(props) => (
                      <CustomSelect
                        {...fields.tools_others}
                        defaultValue={getDefaultValue("tools_others")}
                        errors={errors}
                        onChange={(values) => {
                          props.onChange(values);
                        }}
                      />
                    )}
                  />
                </AnimationWrapper>
                <FieldWrapper className="col-12">
                  <DescriptionField {...fields.review} />
                </FieldWrapper>
              </div>
            </AnimationWrapper>
            <AnimationWrapper className="card override-last" isVisible={isVisible("others", true)}>
              <ClientCard profile={data || profile} location={location} />
            </AnimationWrapper>
            <div className="form-row no-gutters">
              <AnimationWrapper className="col-12" isVisible={isVisible("payment_details", true)}>
                <PaymentDetails
                  ref={paymentDetailsRef}
                  billingEmailCCs={_.get(profile, "billing_email_cc", [])}
                />
              </AnimationWrapper>
            </div>
          </AnimationWrapper>
          <div
            className={`d-flex flex-row align-items-center ${styles.buttons} ${
              isVisible("others", true) ? styles.complete : ""
            }`}
          >
            <input
              type="submit"
              className="btn btn-lilac font-weight-normal"
              disabled={submitting}
              value={!submitting ? "Save Profile" : "Saving..."}
            />
          </div>
        </div>
      </form>
    </div>
  );
}

export default withRouter(EditProfile);
